import { useCallback, useEffect, useState } from 'react';

/**
 * Persist a form's state to localStorage so it survives page reloads or
 * accidental navigation. The hook hydrates from the same key on mount and
 * writes back on every state change.
 *
 * Note: do NOT pass anything that cannot round-trip through JSON (Date, File,
 * Blob). Keep file inputs in a separate non-persisted useState and re-attach
 * after the draft is restored.
 */
export function useFormDraft<T extends object>(
  key: string,
  initial: T,
): [T, (patch: Partial<T> | ((prev: T) => T)) => void, () => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
      return raw ? { ...initial, ...(JSON.parse(raw) as Partial<T>) } : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Quota or privacy mode — silently skip.
    }
  }, [key, state]);

  const update = useCallback((patch: Partial<T> | ((prev: T) => T)) => {
    setState((prev) => (typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }));
  }, []);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // best-effort
    }
    setState(initial);
  }, [key, initial]);

  return [state, update, clear];
}
